import { useState } from 'react';
import type { ReactNode } from 'react';
import { Icone } from '../../design/Icone';
import { ROTULO_DO_RESULTADO, dataCurta, numeroDoCaso, taxaTexto } from '../../auditoria';
import type {
  CasoDaConta,
  ContaDaPessoa,
  NivelDeQualidade,
  ResultadoDaAuditoria,
} from '../../auditoria';
import './qualidade.css';

/**
 * TELA 24 — peças comuns à visão do administrador e à da própria pessoa.
 *
 *   TODA TAXA MOSTRA A CONTA INTEIRA: "3 ÷ 8 = 37,5%", nunca "37%" solto.
 *   Sem conclusiva a taxa é "—" (nunca 0%); abaixo do mínimo é "amostra insuficiente".
 */

/** "3 ÷ 8 = 37,5%". */
export const contaTexto = (erros: number, conclusivas: number, taxa: number) =>
  `${String(erros)} ÷ ${String(conclusivas)} = ${taxaTexto(taxa)}`;

/** A taxa só existe com conclusiva. */
export const taxaOuTraco = (conclusivas: number, taxa: number | null) =>
  conclusivas > 0 && taxa !== null ? taxaTexto(taxa) : '—';

/** "últimas 10 auditorias conclusivas" / "últimos 7 dias". */
export const janelaTexto = (tipo: 'DIAS' | 'ULTIMAS_N', valor: number) =>
  tipo === 'DIAS'
    ? `últimos ${String(valor)} dias`
    : `últimas ${String(valor)} auditorias conclusivas`;

/** "2026-09-22" → "22/09" (dia do calendário, sem fuso). */
export const diaDoCalendario = (dia: string) => `${dia.slice(8, 10)}/${dia.slice(5, 7)}`;

/** "22/09 às 14:20". */
export const quando = (iso: string) =>
  `${dataCurta(iso)} às ${new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  })}`;

export const iniciaisDe = (nome: string | null) =>
  (nome ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');

/** A frase da conta, em português de gente (o `porque` do servidor vem sem acento). */
export function fraseDaConta(c: {
  erros: number;
  conclusivas: number;
  taxa: number;
  amostraOk: boolean;
  minimo: number;
}): string {
  if (c.conclusivas === 0) return 'Nenhuma auditoria conclusiva na janela: a taxa é —, não 0%.';
  if (!c.amostraOk)
    return `${String(c.erros)} de ${String(c.conclusivas)} conclusiva(s): abaixo do mínimo de ${String(c.minimo)} para avaliar — amostra insuficiente, nunca bloqueio.`;
  return `${String(c.erros)} erro(s) confirmado(s) ÷ ${String(c.conclusivas)} auditoria(s) conclusiva(s) = ${taxaTexto(c.taxa)}.`;
}

const TOM_DO_RESULTADO: Readonly<Record<ResultadoDaAuditoria, string>> = {
  BALCONISTA_CORRETO: 'ok',
  ERRO_BALCONISTA_CONFIRMADO: 'erro',
  PROBLEMA_DADOS: 'dados',
  INCONCLUSIVO: 'neutro',
  PRODUTO_MUDOU_LOCAL: 'neutro',
};

export function SeloDoResultado({ r }: { r: ResultadoDaAuditoria }) {
  return <span className={`ql-selo ql-selo-${TOM_DO_RESULTADO[r]}`}>{ROTULO_DO_RESULTADO[r]}</span>;
}

export function Selo({
  tom,
  icone,
  children,
}: {
  tom: 'ok' | 'erro' | 'dados' | 'neutro' | 'aviso';
  icone?: 'lock' | 'warn' | 'check';
  children: ReactNode;
}) {
  return (
    <span className={`ql-selo ql-selo-${tom}`}>
      {icone && <Icone nome={icone} tamanho={11} traco={1.8} />}
      {children}
    </span>
  );
}

/** Cor da taxa: segue o ESTADO (quem decide é o servidor), nunca o número sozinho. */
export function tomDaTaxa(p: {
  nivel: NivelDeQualidade;
  amostraOk: boolean;
  conclusivas: number;
  bloqueada: boolean;
}) {
  if (p.conclusivas === 0 || !p.amostraOk) return 'ql-taxa-apagada';
  if (p.bloqueada || p.nivel === 'BLOQUEIO') return 'ql-taxa-erro';
  if (p.nivel === 'CRITICO' || p.nivel === 'AVISO') return 'ql-taxa-aviso';
  return 'ql-taxa-ok';
}

/** O estado da pessoa. Com a retenção desligada, "bloqueio" nunca aparece como retenção. */
export function EstadoDaPessoa({
  p,
  retencao,
}: {
  p: {
    nivel: NivelDeQualidade;
    amostraOk: boolean;
    conclusivas: number;
    bloqueada: boolean;
  };
  retencao: boolean;
}) {
  if (p.bloqueada)
    return retencao ? (
      <Selo tom="erro" icone="lock">
        Bloqueada · premiação
      </Selo>
    ) : (
      <Selo tom="neutro" icone="lock">
        Bloqueio registrado · retenção desligada
      </Selo>
    );
  if (p.conclusivas === 0) return <Selo tom="neutro">Sem auditoria conclusiva</Selo>;
  if (!p.amostraOk) return <Selo tom="neutro">Amostra insuficiente</Selo>;
  if (p.nivel === 'BLOQUEIO')
    return (
      <Selo tom="erro" icone="warn">
        Acima do limite · sem bloqueio
      </Selo>
    );
  if (p.nivel === 'CRITICO')
    return (
      <Selo tom="aviso" icone="warn">
        Crítico · sem bloqueio
      </Selo>
    );
  if (p.nivel === 'AVISO') return <Selo tom="aviso">Aviso</Selo>;
  return <Selo tom="ok">Dentro do padrão</Selo>;
}

/** Por que o caso ficou de fora — o texto do servidor, com os acentos. */
export function motivoDeFora(m: string | null): string {
  if (!m) return 'não entra na taxa';
  if (m.startsWith('caso reaberto')) return 'reaberto: aguarda conclusão';
  if (m.startsWith('houve movimento')) return 'movimento no período explica';
  if (m.startsWith('a auditoria nao concluiu')) return 'não entra na taxa';
  return m;
}

function LinhaDoCaso({ c, fora, linkar }: { c: CasoDaConta; fora: boolean; linkar: boolean }) {
  const conteudo = (
    <>
      <span className="ql-caso-num">{numeroDoCaso(c.numero)}</span>
      <span className="ql-caso-prod">
        {c.produto} · {c.setor} B{c.bloco}
      </span>
      <SeloDoResultado r={c.resultado} />
      {fora ? (
        <span className="ql-caso-fora" title={c.motivoExclusao ?? undefined}>
          {motivoDeFora(c.motivoExclusao)}
        </span>
      ) : (
        <span className="ql-caso-data">{dataCurta(c.concluidoEm)}</span>
      )}
    </>
  );
  const classe = `ql-caso${fora ? ' ql-caso-off' : ''}`;
  return linkar ? (
    <a className={classe} href={`#/caso?id=${c.divergenciaId}`}>
      {conteudo}
    </a>
  ) : (
    <div className={classe}>{conteudo}</div>
  );
}

/**
 * "Casos considerados": o que entrou no numerador, o que só compôs o denominador e o que ficou
 * FORA do cálculo, com o motivo. Nada some da conta.
 */
export function CasosConsiderados({
  incluidos,
  excluidos,
  linkar = true,
  rodape,
}: {
  incluidos: readonly CasoDaConta[];
  excluidos: readonly CasoDaConta[];
  linkar?: boolean;
  rodape?: ReactNode;
}) {
  const [verCorretos, setVerCorretos] = useState(false);
  const erros = incluidos.filter((c) => c.resultado === 'ERRO_BALCONISTA_CONFIRMADO');
  const corretos = incluidos.filter((c) => c.resultado !== 'ERRO_BALCONISTA_CONFIRMADO');
  return (
    <>
      <div className="ql-casos">
        <div className="ql-casos-cab">INCLUÍDOS NO NUMERADOR · {erros.length}</div>
        {erros.length === 0 && (
          <div className="ql-caso ql-caso-vazio">Nenhum erro confirmado na janela.</div>
        )}
        {erros.map((c) => (
          <LinhaDoCaso key={c.auditoriaId} c={c} fora={false} linkar={linkar} />
        ))}
        {verCorretos && corretos.length > 0 && (
          <>
            <div className="ql-casos-cab">
              SÓ NO DENOMINADOR · BALCONISTA CORRETO · {corretos.length}
            </div>
            {corretos.map((c) => (
              <LinhaDoCaso key={c.auditoriaId} c={c} fora={false} linkar={linkar} />
            ))}
          </>
        )}
        <div className="ql-casos-cab">NÃO CONSIDERADOS · FORA DO CÁLCULO · {excluidos.length}</div>
        {excluidos.length === 0 && (
          <div className="ql-caso ql-caso-vazio">
            Nenhum caso concluído ficou de fora nesta janela.
          </div>
        )}
        {excluidos.map((c) => (
          <LinhaDoCaso key={c.auditoriaId} c={c} fora linkar={linkar} />
        ))}
      </div>
      <p className="ql-nota ql-nota-casos">
        {corretos.length > 0 ? (
          <>
            {corretos.length === 1
              ? 'Uma conclusão favorável também está na janela'
              : `${String(corretos.length)} conclusões favoráveis também estão na janela`}{' '}
            (balconista correto) e compõe{corretos.length === 1 ? '' : 'm'} o denominador de{' '}
            {incluidos.length}.{' '}
            <button
              type="button"
              className="ql-link-botao"
              onClick={() => setVerCorretos((v) => !v)}
            >
              {verCorretos ? 'Esconder' : 'Ver'} {corretos.length === 1 ? 'o caso' : 'os casos'}
            </button>
            {rodape ? ' ' : ''}
          </>
        ) : null}
        {rodape}
      </p>
    </>
  );
}

/** A caixa "a conta inteira" de uma pessoa (perfil e "Minhas auditorias"). */
export function ContaInteira({ c, titulo }: { c: ContaDaPessoa; titulo: string }) {
  return (
    <div className="ql-conta">
      <div className="ql-conta-rot">{titulo}</div>
      <div className="ql-conta-linha">
        <span className={`ql-conta-taxa ${tomDaTaxa({ ...c, bloqueada: c.bloqueio !== null })}`}>
          {taxaOuTraco(c.conclusivas, c.taxa)}
        </span>
        <span className="ql-mono">
          {c.erros} ÷ {c.conclusivas}
        </span>
      </div>
      <div className="ql-conta-porque">{fraseDaConta(c)}</div>
      <div className="ql-conta-janela">
        Janela: {janelaTexto(c.janelaTipo, c.janelaValor)}
        {c.janelaDesde
          ? ` (${diaDoCalendario(c.janelaDesde)} a ${diaDoCalendario(c.janelaAte)})`
          : ''}
        {c.novaJanelaDesde ? ` · reiniciada em ${dataCurta(c.novaJanelaDesde)}` : ''} · mínimo de
        amostra {c.minimo} · limite de bloqueio {c.threshold}%
      </div>
    </div>
  );
}
